<!-- src/views/ZoneForm.vue -->
<template>
  <v-container fluid>
    <!-- HEADER ROW-->
    <v-row>
      <v-col cols="12">
        <!-- Use flexbox with no wrapping so title and buttons always stay on one line -->
        <v-card-title class="d-flex justify-space-between align-center flex-nowrap">
          <div>
            <div class="text-h5 font-weight-bold">
              {{ isEdit ? "Zone" : "Create Zone" }}
            </div>
            <div class="subtitle-2 grey--text">Add or edit a Zone</div>
          </div>
          <v-btn
            v-if="isEdit && props.id"
            size="small"
            variant="text"
            color="primary"
            @click="showHistory = true"
          >
            <v-icon left size="18">mdi-history</v-icon>
            Change History
          </v-btn>
        </v-card-title>
      </v-col>
    </v-row>

    <!-- Body ROW-->
    <v-form ref="form" @submit.prevent="saveZone">
      <v-row class="fill-height">
        <!-- LEFT 33%: Form -->
        <v-col cols="12" md="3" class="mr2">
          <v-card class="pa-4 fill-height" elevation="1">
            <div class="text-h6 mb-4">
              {{ isEdit ? "Details" : "Create Zone" }}
            </div>

            <v-form ref="form" @submit.prevent="saveZone" class="d-flex flex-column fill-height">
              <div class="caption grey--text d-flex align-center">
                Name
                <audit-field-timeline
                  v-if="isEdit && props.id"
                  entity-type="Zone"
                  :record-id="props.id"
                  field-key="Name"
                  field-label="Name"
                />
              </div>
              <div class="subtitle-1 font-weight-bold mb-4">
                <v-text-field
                  v-model="zone.name"
                  required
                  class="mb-4 bg-white"
                  placeholder="Type Here..."
                  variant="filled"
                  rounded="lg"
                  hide-details
                />
              </div>

              <div class="caption grey--text d-flex align-center">
                Description
                <audit-field-timeline
                  v-if="isEdit && props.id"
                  entity-type="Zone"
                  :record-id="props.id"
                  field-key="Description"
                  field-label="Description"
                />
              </div>
              <div class="subtitle-1 font-weight-bold mb-4">
                <v-textarea v-model="zone.description" required placeholder="Type Here..." />
              </div>

              <div class="caption grey--text d-flex align-center">
                Zone Type
                <audit-field-timeline
                  v-if="isEdit && props.id"
                  entity-type="Zone"
                  :record-id="props.id"
                  field-key="TypeId"
                  field-label="Zone Type"
                />
              </div>
              <div class="subtitle-1 font-weight-bold mb-4">
                <v-select
                  v-model="zone.typeId"
                  :items="zoneTypes"
                  item-title="zoneType"
                  item-value="zoneTypeId"
                  required
                  dense
                  solo
                />
              </div>

              <div class="caption grey--text d-flex align-center">
                Owner
                <audit-field-timeline
                  v-if="isEdit && props.id"
                  entity-type="Zone"
                  :record-id="props.id"
                  field-key="OwnerId"
                  field-label="Owner"
                />
              </div>
              <div class="subtitle-1 font-weight-bold mb-4">
                <v-select
                  v-model="zone.ownerId"
                  :items="users"
                  item-title="userName"
                  item-value="id"
                  required
                  dense
                  solo
                />
              </div>

              <div class="caption grey--text">Assigned To Team/s</div>
              <div class="subtitle-1 font-weight-bold mb-4">
                <v-select
                  v-model="zone.teamIds"
                  :items="teams"
                  item-title="name"
                  item-value="id"
                  multiple
                  clearable
                  dense
                  solo
                >
                  <template #selection="{ item, index }">
                    <v-chip
                      :key="index"
                      class="ma-1"
                      color="blue lighten-4"
                      text-color="black"
                      variant="elevated"
                      size="small"
                      closable
                      @click:close="zone.teamIds.splice(index, 1)"
                    >
                      {{ item.raw.name }}
                    </v-chip>
                  </template>
                </v-select>
              </div>

              <v-chip
                v-for="id in zone.teamIds"
                :key="id"
                class="ma-1"
                small
                color="blue lighten-4"
                variant="elevated"
              >
                {{ teams.find((t) => t.id === id)?.name || "Unknown" }}
              </v-chip>

              <!-- Colour picker -->
              <div class="caption grey--text mb-2 d-flex align-center">
                Colour
                <audit-field-timeline
                  v-if="isEdit && props.id"
                  entity-type="Zone"
                  :record-id="props.id"
                  field-key="Colour"
                  field-label="Colour"
                />
              </div>
              <div class="d-flex flex-wrap mb-6">
                <div
                  v-for="c in colours"
                  :key="c"
                  class="color-circle"
                  :class="{
                    active: zone.colour && zone.colour.toLowerCase() === c.toLowerCase(),
                  }"
                  :style="{ backgroundColor: c }"
                  @click="zone.colour = c"
                />
              </div>

              <div class="d-flex justify-end">
                <v-btn 
                  v-if="isEdit" 
                  color="error" 
                  variant="text" 
                  @click="deleteDialog = true"
                >
                  <v-icon left size="18">mdi-delete</v-icon>
                  Delete
                </v-btn>
                <v-btn text @click="cancel">Cancel</v-btn>
                <v-spacer />
                <v-btn color="success" :loading="savingZone" :disabled="savingZone" @click="saveZone">
                  {{ isEdit ? "Save" : "Create Zone" }}
                </v-btn>
              </div>
            </v-form>
          </v-card>
        </v-col>

        <!-- RIGHT 66%: Map + toolbar -->

        <v-col cols="12" md="9" class="">
          <v-card
            flat
            class="pa-4 rounded-lg fill-height"
            style="position: relative; overflow: hidden"
          >
            <div class="font-weight-bold mb-2" style="margin-left: 16px">Create Zone</div>

            <!-- toolbar overlay -->
            <div
              class="map-toolbar pa-2 rounded d-flex gap-2"
              style="position: absolute; top: 16px; left: 16px; background: white; z-index: 1000; box-shadow: 0 2px 4px rgba(0,0,0,0.1); pointer-events: auto;"
            >
              <v-btn
                size="small"
                color="primary"
                @click="fileInput?.click()"
              >
                <v-icon left>mdi-upload</v-icon>
                Import GeoJSON
              </v-btn>
              <input
                ref="fileInput"
                type="file"
                accept=".geojson,.json"
                style="display: none"
                @change="onFileSelected"
              />
              <!-- Offline map generation -->
              <v-btn
                v-if="isEdit && props.id"
                size="small"
                color="success"
                :loading="generatingMap"
                :disabled="generatingMap || checkingMapStatus"
                @click="handleGenerateOfflineMap"
              >
                <v-icon left>mdi-map-download</v-icon>
                {{ hasOfflineMap ? 'Regenerate Offline Map' : 'Create Offline Map' }}
              </v-btn>
            </div>

            <zone-map
              class="map-embed"
              :center="computedMapCenter"
              :zoom="props.isEdit && props.id ? 12 : mapZoom"
              :geojson="overlays[props.id] || null"
              @draw-created="onDrawCreated"
              @draw-edited="onDrawEdited"
              @draw-deleted="onDrawDeleted"
              @map-center-changed="onMapCenterChanged"
            />
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Delete Zone</v-card-title>
        <v-card-text>
          Are you sure you want to delete "<strong>{{ zone.name }}</strong>"? 
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Field-level change history (Enhanced Audit Trail) -->
    <audit-history-modal
      v-if="isEdit && props.id"
      v-model="showHistory"
      entity-type="Zone"
      :record-id="props.id"
      title="Zone Change History"
    />
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useDialogStore } from "@/stores/dialog";
import L from "leaflet";
import { fetchZoneTypes, fetchZone, createZone, updateZone, deleteZone, fetchZoneGeoJson, generateOfflineMap, checkOfflineMapStatus as checkOfflineMapStatusApi } from "@/api/zones";
import { fetchUsers } from "@/api/dropdowns";
import { sendGeoJson, uploadGeoJsonFile } from "@/api/overlays";
import { getTeams } from "@/api/teams";
import ZoneMap from "@/components/ZoneMapDraw.vue";
import AuditHistoryModal from "@/components/audit/AuditHistoryModal.vue";
import AuditFieldTimeline from "@/components/audit/AuditFieldTimeline.vue";

const props = defineProps({
  isEdit: Boolean,
  id: [String],
});

const colours = [
  "#1E88E5",
  "#E53935",
  "#FB8C00",
  "#2E7D32",
  "#8E24AA",
  "#EC407A",
  "#26C6DA",
  "#EEEEEE",
];

const router = useRouter();
const toast = useToast();
const dialog = useDialogStore();

const form = ref(null);
const zone = ref({
  name: "",
  typeId: null,
  description: "",
  geometry: null,
  ownerId: null,
  colour: null,
  teamIds: [],
});
const zoneTypes = ref([]);
const users = ref([]);
const overlays = ref({});
const pendingOverlayGeoJson = ref(null);
const teams = ref([]);
const fileInput = ref(null);
const generatingMap = ref(false);
const savingZone = ref(false);
const hasOfflineMap = ref(false);
const checkingMapStatus = ref(false);
const deleteDialog = ref(false);
const showHistory = ref(false);
const mapCenter = ref([53.41291, -8.24389]); // Default: Center of Ireland
const mapZoom = ref(8); // Default zoom level

async function loadZoneTypes() {
  const data = await fetchZoneTypes(); //api call
  zoneTypes.value = data;
}

async function loadUsers() {
  users.value = await fetchUsers();
}

async function loadTeams() {
  const list = await getTeams();
  teams.value = [{ id: 0, name: "All" }, ...list]; // prepend All
}

function onDrawCreated(geojson) {
  pendingOverlayGeoJson.value = geojson;
  if (props.id) {
    overlays.value[props.id] = geojson;
    saveOverlay(geojson, props.id);
  }
}
function onDrawEdited(geojson) {
  pendingOverlayGeoJson.value = geojson;
  if (props.id) {
    overlays.value[props.id] = geojson;
    saveOverlay(geojson, props.id);
  }
}
function onDrawDeleted(geojson) {
  pendingOverlayGeoJson.value = geojson?.features?.length ? geojson : null;
  if (props.id) {
    overlays.value[props.id] = pendingOverlayGeoJson.value;
  }
}

async function onFileSelected(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  // Validate file type
  const extension = file.name.toLowerCase().split('.').pop();
  if (extension !== 'geojson' && extension !== 'json') {
    await dialog.confirm({
      title: "Invalid File Type",
      message: 'Please select a GeoJSON file (.geojson or .json)',
      variant: "error",
      buttons: "ok"
    });
    return;
  }

  // For new zones, we need a zone ID first
  if (!props.isEdit || !props.id) {
    await dialog.confirm({
      title: "Zone Not Saved",
      message: 'Please save the zone first before uploading overlays.',
      variant: "warn",
      buttons: "ok"
    });
    return;
  }

  const zoneId = props.id;
  if (!zoneId) {
    await dialog.confirm({
      title: "Zone ID Required",
      message: 'Zone ID is required to upload overlay.',
      variant: "error",
      buttons: "ok"
    });
    return;
  }

  try {
    // Upload file
    const result = await uploadGeoJsonFile(file, zoneId);

    // Reload overlays to show the new one
    await loadOverlays();

    // Reset file input
    if (event.target) {
      event.target.value = '';
    }
  } catch (error) {
    await dialog.confirm({
      title: "Upload Failed",
      message: `Failed to upload file: ${error.response?.data?.message || error.message}`,
      variant: "error",
      buttons: "ok"
    });
  }
}

async function loadOverlays() {
  // For new zones, skip loading overlays
  if (!props.id || !props.isEdit) return;
  
  try {
    const overlayResp = await fetchZoneGeoJson(props.id);
    
    // fetchZoneGeoJson already parses and returns the first overlay's GeoJSON
    // If you want to support multiple overlays, you'd need to modify the API
    // For now, we just use what's returned
    let geojson = overlayResp;

    if (geojson && geojson.features) {
      geojson.features.forEach((f) => {
        if (!f.properties) f.properties = {};
        // Optionally set color from zone
        if (zone.value.colour) {
          f.properties.colour = zone.value.colour;
        }
      });
    }

    overlays.value[props.id] = geojson;
  } catch (overlayErr) {
    overlays.value[props.id] = null;
  }
}

async function saveOverlay(geojson, zoneId = props.id) {
  if (!zoneId || !geojson) return;

  const payload = {
    // Id can be omitted or set to 0/null for a new record
    Id: 0,
    CustomerId: 0,
    ZoneId: zoneId,
    OverlayName: "Zone boundary",
    Created: new Date().toISOString(),
    Source: "UserDrawn",
    Type: "ZONE",
    Data: JSON.stringify(geojson),
    IsLocationLevel: false,
    IsVisible: true,
    Colour: zone.value.colour,
    Url: null,
    MobileFileName: null,
    MobileFileData: null,
    CommonVideoName: null,
    VideoOrder: null,
  };

  await sendGeoJson(payload);
  //await axios.post("https://ww2api.evolve-red.com/api/Overlays", payload);
}
async function loadExistingZone() {
  if (!props.isEdit) return;
  const dto = await fetchZone(props.id);
  zone.value.name = dto.name;
  zone.value.typeId = dto.typeId;
  zone.value.description = dto.description;
  zone.value.geometry = dto.geometry;
  zone.value.ownerId = dto.ownerId;
  zone.value.teamIds = dto.teamIds ?? [];
  let savedColour = dto.colour ?? dto.Colour ?? "";
  zone.value.colour = savedColour.trim();
}

async function saveZone() {
  if (!form.value.validate()) return;
  savingZone.value = true;
  try {
    // Filter out invalid team IDs (0 or negative) before sending
    const zoneData = {
      ...zone.value,
      teamIds: zone.value.teamIds?.filter(id => id > 0) || []
    };
    
    if (props.isEdit) {
      await updateZone(props.id, zoneData); //api call
    } else {
      toast.info("Saving zone details...");
      const createdZone = await createZone(zoneData); //api call
      const createdZoneId = createdZone?.zoneId ?? createdZone?.ZoneId ?? createdZone?.id ?? createdZone?.Id;

      if (createdZoneId && pendingOverlayGeoJson.value) {
        toast.info("Saving zone boundary...");
        overlays.value[createdZoneId] = pendingOverlayGeoJson.value;
        await saveOverlay(pendingOverlayGeoJson.value, createdZoneId);
      }
    }
    toast.success(props.isEdit ? "Zone updated successfully" : "Zone created successfully");
    router.push({ name: "ZoneList" });
  } catch (error) {
    console.error("Error saving zone:", error);
    const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || "Failed to save zone";
    await dialog.confirm({
      title: "Save Failed",
      message: errorMessage,
      variant: "error",
      buttons: "ok"
    });
  } finally {
    savingZone.value = false;
  }
}

async function onAdd() {}

function cancel() {
  router.back();
}

// Save map center to localStorage
function saveMapCenter(center, zoom) {
  try {
    localStorage.setItem('zoneMapCenter', JSON.stringify(center));
    localStorage.setItem('zoneMapZoom', JSON.stringify(zoom));
  } catch (err) {
    console.log('Failed to save map center to localStorage:', err);
  }
}

// Load map center from localStorage
function loadMapCenter() {
  try {
    const savedCenter = localStorage.getItem('zoneMapCenter');
    const savedZoom = localStorage.getItem('zoneMapZoom');
    if (savedCenter) {
      const center = JSON.parse(savedCenter);
      if (Array.isArray(center) && center.length === 2) {
        mapCenter.value = center;
      }
    }
    if (savedZoom) {
      const zoom = JSON.parse(savedZoom);
      if (typeof zoom === 'number') {
        mapZoom.value = zoom;
      }
    }
  } catch (err) {
    console.log('Failed to load map center from localStorage:', err);
  }
}

// Handle map center changes from the map component
// Use a flag to prevent recursive updates
let isUpdatingFromMap = false;

function onMapCenterChanged({ center, zoom }) {
  // Prevent recursive updates - if we're already updating, skip
  if (isUpdatingFromMap) return;
  
  // CRITICAL: Check geometry FIRST - if geometry exists, NEVER update mapCenter
  // This prevents fitBounds() from overwriting the geometry center
  if (props.isEdit && props.id && overlays.value[props.id]) {
    // Geometry exists - don't update mapCenter, geometry controls the view
    return;
  }
  
  // Only save if no geometry has been drawn
  isUpdatingFromMap = true;
  mapCenter.value = center;
  if (zoom) mapZoom.value = zoom;
  saveMapCenter(center, zoom);
  // Reset flag after next tick to allow computed to update
  setTimeout(() => {
    isUpdatingFromMap = false;
  }, 50);
}

async function confirmDelete() {
  if (!props.id || !props.isEdit) return;

  try {
    await deleteZone(props.id);
    toast.success(`Zone "${zone.value.name}" has been deleted successfully.`, {
      timeout: 3000,
      icon: "mdi-check-circle"
    });
    deleteDialog.value = false;
    router.push({ name: "ZoneList" });
  } catch (error) {
    console.error("Error deleting zone:", error);
    const errorMessage = error.response?.data?.message || error.message || "Failed to delete zone";
    await dialog.confirm({
      title: "Delete Failed",
      message: errorMessage,
      variant: "error",
      buttons: "ok"
    });
  }
}

async function handleGenerateOfflineMap() {
  if (!props.id || !props.isEdit) {
    await dialog.confirm({
      title: "Zone Not Saved",
      message: 'Please save the zone first before generating an offline map.',
      variant: "warn",
      buttons: "ok"
    });
    return;
  }

  if (!overlays.value[props.id]) {
    await dialog.confirm({
      title: "Zone Boundary Required",
      message: 'Please draw the zone boundary first before generating an offline map.',
      variant: "warn",
      buttons: "ok"
    });
    return;
  }

  // If map already exists, confirm before regenerating
  if (hasOfflineMap.value) {
    const result = await dialog.confirm({
      title: "Offline Map Exists",
      message: "An offline map already exists for this zone. Regenerating will replace it. Do you want to continue?",
      variant: "warn",
      buttons: "ok-cancel"
    });
    if (result !== "ok") return;
  }

  generatingMap.value = true;
  try {
    const response = await generateOfflineMap(props.id);
    toast.success('Offline map generation started successfully! This may take a few minutes. You will be notified when it\'s complete.');
    // Check status after a delay
    setTimeout(async () => {
      await checkOfflineMapStatus();
    }, 2000);
  } catch (error) {
    let errorMessage = 'Failed to generate offline map';
    
    // Extract error message from various response formats
    if (error.response?.data) {
      const data = error.response.data;
      // Try different possible error response formats
      errorMessage = data.message 
        || data.error 
        || (typeof data === 'string' ? data : null)
        || error.response.statusText 
        || `Server error: ${error.response.status}`;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    // Add details if available
    if (error.response?.data?.details) {
      errorMessage += `\n\nDetails: ${error.response.data.details}`;
    }
    
    await dialog.confirm({
      title: "Generation Failed",
      message: errorMessage,
      variant: "error",
      buttons: "ok"
    });
  } finally {
    generatingMap.value = false;
  }
}

async function checkOfflineMapStatus() {
  if (!props.id || !props.isEdit) return;
  
  checkingMapStatus.value = true;
  try {
    const response = await checkOfflineMapStatusApi(props.id);
    hasOfflineMap.value = response.hasOfflineMap || false;
  } catch (error) {
    hasOfflineMap.value = false;
  } finally {
    checkingMapStatus.value = false;
  }
}

async function initializeMapCenter() {
  // Check if geometry exists before initializing (geometry takes priority)
  const hasGeometry = props.isEdit && props.id && overlays.value[props.id];
  if (hasGeometry) {
    console.log("Geometry exists, skipping map center initialization");
    return; // Don't override geometry center
  }

  // Try browser geolocation first (most accurate, requires permission)
  if (navigator.geolocation) {
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          { timeout: 5000, maximumAge: 60000 }
        );
      });
      // TRIPLE-check geometry hasn't been loaded while we were waiting
      const stillNoGeometry = !props.isEdit || !props.id || !overlays.value[props.id];
      if (!stillNoGeometry) {
        console.log("Geometry loaded during geolocation, aborting map center update");
        return; // Geometry was loaded, abort
      }
      mapCenter.value = [position.coords.latitude, position.coords.longitude];
      console.log("Map centered using browser geolocation:", mapCenter.value);
      return;
    } catch (err) {
      console.log("Browser geolocation failed, trying IP geolocation...", err);
    }
  }

  // Fallback: Try IP-based geolocation (no permission needed)
  // COMMENTED OUT - not using IP location
  // try {
  //   const response = await fetch("https://ipapi.co/json/");
  //   const data = await response.json();
  //   if (data.latitude && data.longitude) {
  //     // TRIPLE-check geometry hasn't been loaded while we were waiting
  //     const stillNoGeometry = !props.isEdit || !props.id || !overlays.value[props.id];
  //     if (!stillNoGeometry) {
  //       console.log("Geometry loaded during IP geolocation, aborting map center update");
  //       return; // Geometry was loaded, abort
  //     }
  //     mapCenter.value = [data.latitude, data.longitude];
  //     console.log("Map centered using IP geolocation:", mapCenter.value);
  //     return;
  //   }
  // } catch (err) {
  //   console.log("IP geolocation failed, using Ireland default...", err);
  // }

  // Final fallback: Center of Ireland (already set as default)
  // TRIPLE-check geometry hasn't been loaded
  const stillNoGeometry = !props.isEdit || !props.id || !overlays.value[props.id];
  if (!stillNoGeometry) {
    console.log("Geometry loaded before default center, aborting map center update");
    return; // Geometry was loaded, abort
  }
  mapCenter.value = [53.41291, -8.24389];
  console.log("Map centered to Ireland default:", mapCenter.value);
}

onMounted(async () => {
  // Load saved map center first (before initializing)
  loadMapCenter();
  
  await Promise.all([loadZoneTypes(), loadUsers(), loadTeams(), loadExistingZone()]);
  await loadOverlays();
  
  // Initialize map center after overlays are loaded
  // If there's no geometry, we'll use the initialized center
  // If there is geometry, getZoneCenter will use that instead
  const hasGeometry = props.isEdit && props.id && overlays.value[props.id];
  if (!hasGeometry) {
    await initializeMapCenter();
  }
  
  if (props.isEdit && props.id) {
    await checkOfflineMapStatus();
  }
});

function getZoneCenter(geojson) {
  if (!geojson) return mapCenter.value; // Use the initialized center instead of hardcoded Limerick
  try {
    const tempLayer = L.geoJSON(geojson);
    const bounds = tempLayer.getBounds();
    if (bounds.isValid()) {
      return [bounds.getCenter().lat, bounds.getCenter().lng];
    }
  } catch (e) {
  }
  // Fallback to initialized center
  return mapCenter.value;
}

// Computed property to prevent reactive updates when geometry exists
// Cache the last computed value to avoid unnecessary re-renders
let lastComputedCenter = null;
const computedMapCenter = computed(() => {
  const geo = overlays.value[props.id];
  let newCenter;
  
  if (geo) {
    // If geometry exists, always use it (never fall back to mapCenter)
    newCenter = getZoneCenter(geo);
  } else {
    // Only use mapCenter if no geometry
    newCenter = mapCenter.value;
  }
  
  // Return cached value if coordinates haven't changed (prevents unnecessary re-renders)
  if (lastComputedCenter && 
      lastComputedCenter[0] === newCenter[0] && 
      lastComputedCenter[1] === newCenter[1]) {
    return lastComputedCenter;
  }
  
  lastComputedCenter = newCenter;
  return newCenter;
});
</script>
<style scoped>
.color-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin: 4px;
  cursor: pointer;
  border: 4px solid transparent;
  transition: border-color 0.2s;
}
.color-circle.active {
  border-color: rgba(0, 0, 0, 0.6);
}

.map-embed {
  width: calc(100% - 20px);
  height: calc(100% - 24px); /* subtract title height + margin */
  border-radius: 20px;
  overflow: hidden;
}

.map-wrapper {
  margin: 10px;
  min-height: 500px;
}

.map-toolbar {
  pointer-events: auto !important;
  z-index: 1000 !important;
}

.map-toolbar .v-btn {
  pointer-events: auto !important;
}
</style>
